import React, { useState, useRef, useEffect, useMemo } from 'react';
import { FaRobot, FaUser, FaPaperPlane, FaTimes, FaSpinner, FaDownload, FaFile, FaFilePdf, FaFileExcel, FaFileImage } from 'react-icons/fa';
import { useUser } from '@clerk/clerk-react';
import Modal from './Modal';
import Button from './Button';
import { useSucursal } from '../../context/SucursalContext';
import { useSimpleEmpresa } from '../../hooks/useSimpleEmpresa';
import { useChatIAConfig } from '../../hooks/useChatIAConfig';
import { usePermisos } from '../../hooks/usePermisos';
import { sendChatMessage, getFallbackResponse } from '../../services/chatApiService';

/**
 * Componente de Chat IA flotante con OpenAI
 * 
 * ACTUALIZACIÓN: Ahora usa OpenAI directamente en el backend
 * - Backend procesa con OpenAI GPT-4o-mini + consultas Supabase específicas
 * - N8N comentado en el backend para posible uso futuro
 * 
 * Proporciona una interfaz de chat inteligente que se adapta al contexto del usuario:
 * - Para clientes: consultas personales sobre sus vehículos
 * - Para admins: consultas empresariales y estadísticas del taller
 */

// Función para detectar si el contenido contiene HTML
const isHTMLContent = (content) => {
  if (!content || typeof content !== 'string') return false;
  
  // Detectar tags HTML comunes que indican contenido HTML estructurado
  const htmlTags = /<(table|tbody|thead|tr|td|th|ul|ol|li|p|div|span|strong|b|em|i|h[1-6]|br)\b[^>]*>/i;
  return htmlTags.test(content);
};

// Función para convertir HTML a texto plano con formato mejorado para tablas
const htmlToText = (html) => {
  if (!html || typeof html !== 'string') return '';
  
  let result = html;
  
  // Detectar y procesar tablas especialmente
  const tableRegex = /<table[^>]*>(.*?)<\/table>/gis;
  result = result.replace(tableRegex, (match, tableContent) => {
    let tableText = '\n';
    
    // Procesar encabezados (th)
    const headerRegex = /<th[^>]*>(.*?)<\/th>/gi;
    const headers = [];
    let headerMatch;
    while ((headerMatch = headerRegex.exec(tableContent)) !== null) {
      headers.push(headerMatch[1].trim());
    }
    
    if (headers.length > 0) {
      tableText += '📋 ' + headers.join(' | ') + '\n';
      tableText += '─'.repeat(headers.join(' | ').length) + '\n';
    }
    
    // Procesar filas (tr con td)
    const rowRegex = /<tr[^>]*>(.*?)<\/tr>/gis;
    let rowMatch;
    while ((rowMatch = rowRegex.exec(tableContent)) !== null) {
      const rowContent = rowMatch[1];
      
      // Solo procesar filas que contienen td (no th)
      if (rowContent.includes('<td')) {
        const cellRegex = /<td[^>]*>(.*?)<\/td>/gi;
        const cells = [];
        let cellMatch;
        while ((cellMatch = cellRegex.exec(rowContent)) !== null) {
          cells.push(cellMatch[1].trim());
        }
        
        if (cells.length > 0) {
          tableText += '  ' + cells.join(' | ') + '\n';
        }
      }
    }
    
    return tableText + '\n';
  });
  
  // Convertir otros elementos HTML
  result = result
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<p[^>]*>/gi, '')
    .replace(/<\/p>/gi, '\n\n')
    .replace(/<strong[^>]*>/gi, '**')
    .replace(/<\/strong>/gi, '**')
    .replace(/<b[^>]*>/gi, '**')
    .replace(/<\/b>/gi, '**')
    .replace(/<em[^>]*>/gi, '*')
    .replace(/<\/em>/gi, '*')
    .replace(/<i[^>]*>/gi, '*')
    .replace(/<\/i>/gi, '*')
    .replace(/<h[1-6][^>]*>/gi, '\n**')
    .replace(/<\/h[1-6]>/gi, '**\n')
    .replace(/<ul[^>]*>/gi, '\n')
    .replace(/<\/ul>/gi, '\n')
    .replace(/<ol[^>]*>/gi, '\n')
    .replace(/<\/ol>/gi, '\n')
    .replace(/<li[^>]*>/gi, '• ')
    .replace(/<\/li>/gi, '\n')
    // Remover atributos y tags restantes
    .replace(/<[^>]+>/g, '')
    // Limpiar espacios extra y saltos de línea múltiples
    .replace(/\n\s*\n\s*\n/g, '\n\n')
    .replace(/^\s+|\s+$/g, '')
    .trim();
  
  return result;
};

// Función para detectar archivos en la respuesta
const detectFiles = (content) => {
  if (!content || typeof content !== 'string') return [];
  
  const filePatterns = {
    // PDF base64: ```pdf filename base64data ```
    pdf: /```pdf\s+(.*?)\s+(.*?)\s+```/gs,
    // Excel: ```xlsx filename base64data ```
    excel: /```xlsx?\s+(.*?)\s+(.*?)\s+```/gs,
    // Imagen: ```image filename base64data ```
    image: /```(?:jpg|jpeg|png|gif|webp)\s+(.*?)\s+(.*?)\s+```/gs,
    // Enlaces de descarga: [Descargar archivo](url)
    downloadLink: /\[Descargar\s+(.*?)\]\((https?:\/\/.*?)\)/g,
    // Base64 directo: data:mime;base64,data
    base64Direct: /data:([^;]+);base64,([A-Za-z0-9+/=]+)/g
  };
  
  const files = [];
  
  // Detectar archivos por patrones
  for (const [type, pattern] of Object.entries(filePatterns)) {
    const matches = [...content.matchAll(pattern)];
    for (const match of matches) {
      if (type === 'downloadLink') {
        files.push({
          type: 'download',
          name: match[1].trim(),
          url: match[2],
          originalMatch: match[0]
        });
      } else if (type === 'base64Direct') {
        const mimeType = match[1];
        const fileExt = mimeType.split('/')[1] || 'file';
        files.push({
          type: fileExt,
          name: `archivo.${fileExt}`,
          data: match[2],
          mimeType: mimeType,
          originalMatch: match[0]
        });
      } else {
        files.push({
          type,
          name: match[1].trim(),
          data: match[2].trim(),
          originalMatch: match[0]
        });
      }
    }
  }
  
  return files;
};

// Función para limpiar el contenido removiendo los archivos detectados
const cleanContentFromFiles = (content, files) => {
  let cleanedContent = content;
  
  // Remover las marcas de archivos del contenido
  files.forEach(file => {
    cleanedContent = cleanedContent.replace(file.originalMatch, '');
  });
  
  return cleanedContent.trim();
};

// Función para descargar archivo
const downloadFile = (file) => {
  try {
    if (file.url) {
      // Descargar desde URL
      window.open(file.url, '_blank');
    } else if (file.data) {
      // Descargar desde base64
      const mimeType = file.mimeType || getMimeType(file.type);
      const byteCharacters = atob(file.data);
      const byteNumbers = new Array(byteCharacters.length);
      
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: mimeType });
      
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  } catch (error) {
    console.error('Error descargando archivo:', error);
  }
};

// Función para obtener MIME type por extensión
const getMimeType = (type) => {
  const mimeTypes = {
    pdf: 'application/pdf',
    excel: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    xls: 'application/vnd.ms-excel',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    webp: 'image/webp'
  };
  return mimeTypes[type.toLowerCase()] || 'application/octet-stream';
};

// Componente para mostrar el icono del archivo según su tipo
const FileIcon = ({ type }) => {
  switch (type.toLowerCase()) {
    case 'pdf':
      return <FaFilePdf className="text-red-500" />;
    case 'excel':
    case 'xlsx':
    case 'xls':
      return <FaFileExcel className="text-green-500" />;
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
    case 'webp':
    case 'image':
      return <FaFileImage className="text-blue-500" />;
    default:
      return <FaFile className="text-gray-500" />;
  }
};

// Componente para mostrar archivos adjuntos
const FileAttachment = ({ file }) => {
  return (
    <div className="mt-2 flex items-center gap-2 p-3 bg-gray-600 rounded-lg border border-gray-500">
      <FileIcon type={file.type} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-100 truncate">{file.name}</p>
        <p className="text-xs text-gray-400">
          {file.type.toUpperCase()} • {file.url ? 'Enlace' : 'Archivo adjunto'}
        </p>
      </div>
      <button
        onClick={() => downloadFile(file)}
        className="flex items-center gap-1 px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded transition-colors"
      >
        <FaDownload className="w-3 h-3" />
        Descargar
      </button>
    </div>
  );
};

// Función para procesar el contenido de respuesta
const processResponseContent = (content) => {
  if (!content) return { content: '', files: [] };
  
  // Detectar archivos primero
  const files = detectFiles(content);
  
  // Limpiar contenido de las marcas de archivos
  let processedContent = cleanContentFromFiles(content, files);
  
  // Procesar HTML si es necesario
  if (isHTMLContent(processedContent)) {
    console.log('🔄 Convirtiendo contenido HTML a texto plano');
    processedContent = htmlToText(processedContent);
  }
  
  if (files.length > 0) {
    console.log('📁 Archivos detectados:', files);
  }
  
  return {
    content: processedContent,
    files: files
  };
};
const ChatIA = () => {
  const { user, isLoaded: userLoaded } = useUser();
  const { selectedSucursal, sucursalData } = useSucursal();
  const { empresaActual: currentEmpresa, usuarioEmpresa, isSuperAdmin } = useSimpleEmpresa();
  const { chatIAEnabled, loading: chatConfigLoading } = useChatIAConfig();
  const { isAdmin, isCliente } = usePermisos();
  
  // Obtener sucursal_id correcta (selectedSucursal es el ID)
  const currentSucursalId = selectedSucursal || sucursalData?.id || null;
  
  // Estados del chat
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [sidebarMenuOpen, setSidebarMenuOpen] = useState(false);
  
  // Referencias
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  
  // Determinar contexto del usuario usando el sistema de permisos (memoizado)
  const isAdminUser = isAdmin();
  const isClientUser = isCliente();
  
  // Usar únicamente el sistema de permisos correcto
  const isLikelyAdmin = useMemo(() => {
    return isAdminUser || isSuperAdmin;
  }, [isAdminUser, isSuperAdmin]);
  
  // Debug adicional - solo cuando cambian valores relevantes
  useEffect(() => {
    console.log('🔍 [ChatIA] Debug de permisos (cambio detectado):', {
      isAdminUser,
      isClientUser,
      isLikelyAdmin,
      userLoaded,
      userFirstName: user?.firstName,
      currentEmpresa: currentEmpresa?.nombre,
      hasEmpresa: !!currentEmpresa,
      // Debug adicional para entender el problema
      usuarioEmpresa,
      perfilId: usuarioEmpresa?.perfil_id,
      isSuperAdminValue: isSuperAdmin
    });
  }, [isAdminUser, isClientUser, isLikelyAdmin, userLoaded, user?.firstName, currentEmpresa?.nombre, usuarioEmpresa, isSuperAdmin]);
  
  // Scroll automático al final de los mensajes
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  
  // Enfocar input cuando se abre el modal
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);
  
  // Mensaje de bienvenida según el contexto (memoizado)
  const getWelcomeMessage = useMemo(() => {
    if (isLikelyAdmin) {
      const sucursalInfo = currentSucursalId && sucursalData ? 
        ` y sucursal *${sucursalData.nombre || sucursalData.Nombre}*` : '';
      
      return {
        id: 'welcome',
        type: 'assistant',
        content: `¡Hola! 👋 Soy tu asistente IA especializado en talleres automotrices.\n\nComo administrador de *${currentEmpresa?.nombre || 'tu empresa'}*${sucursalInfo}, puedo ayudarte con:\n\n• 📊 Estadísticas y reportes del taller\n• 🔍 Consultas sobre clientes específicos\n• 💰 Análisis de ingresos y servicios más solicitados\n• 👨‍🔧 Información sobre mecánicos y productividad\n• 📈 Tendencias y métricas del negocio\n\n¿En qué puedo ayudarte hoy?`,
        timestamp: new Date().toISOString()
      };
    } else {
      return {
        id: 'welcome',
        type: 'assistant', 
        content: `¡Hola ${user?.firstName || ''}! 👋 Soy tu asistente personal para el taller.\n\nPuedo ayudarte con:\n\n• 🚗 Información sobre tus vehículos\n• 📅 Historial de mantenimientos\n• 💵 Consultas sobre precios de servicios\n• 📆 Estado de tus citas programadas\n• ⚡ Recomendaciones de mantenimiento\n\n¿Qué te gustaría saber?`,
        timestamp: new Date().toISOString()
      };
    }
  }, [isLikelyAdmin, currentEmpresa?.nombre, currentSucursalId, sucursalData?.nombre, sucursalData?.Nombre, user?.firstName]);
  
  // Inicializar chat con mensaje de bienvenida
  useEffect(() => {
    if (userLoaded && user && messages.length === 0) {
      setMessages([getWelcomeMessage]);
    }
  }, [userLoaded, user, getWelcomeMessage]);
  
  // Actualizar mensaje de bienvenida cuando cambie el contexto del usuario
  useEffect(() => {
    if (userLoaded && user && messages.length > 0 && messages[0].id === 'welcome') {
      console.log('🔄 [ChatIA] Actualizando mensaje de bienvenida:', {
        isLikelyAdmin,
        currentEmpresa: currentEmpresa?.nombre,
        previousMessageType: messages[0].content?.includes('administrador') ? 'admin' : 'cliente',
        newMessageType: getWelcomeMessage.content?.includes('administrador') ? 'admin' : 'cliente'
      });
      
      setMessages(prevMessages => [
        getWelcomeMessage,
        ...prevMessages.slice(1)
      ]);
    }
  }, [getWelcomeMessage, userLoaded, user, isLikelyAdmin, currentEmpresa?.nombre]);
  
  // Enviar mensaje usando el servicio
  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;
    
    const userMessage = {
      id: Date.now().toString(),
      type: 'user',
      content: inputMessage.trim(),
      timestamp: new Date().toISOString()
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    resetTextareaHeight(); // Reset height after clearing input
    setIsLoading(true);
    setIsTyping(true);
    
    try {
      // Preparar contexto enriquecido para el backend
      const context = {
        sucursal_id: currentSucursalId,
        sucursalId: currentSucursalId, // Alias para compatibilidad
        user_type: isLikelyAdmin ? 'admin' : 'cliente',
        userType: isLikelyAdmin ? 'admin' : 'cliente', // Alias para compatibilidad
        empresa_nombre: currentEmpresa?.nombre,
        empresaNombre: currentEmpresa?.nombre, // Alias para compatibilidad
        empresa_id: currentEmpresa?.id,
        empresaId: currentEmpresa?.id, // Alias para compatibilidad
        user_id: user?.id,
        userId: user?.id, // Alias para compatibilidad
        user_name: user?.firstName || user?.fullName,
        userName: user?.firstName || user?.fullName, // Alias para compatibilidad
        sucursal_nombre: sucursalData?.nombre || sucursalData?.Nombre, // Agregar nombre de sucursal
        plan_id: currentEmpresa?.plan_id, // Plan actual de la empresa
        planId: currentEmpresa?.plan_id // Alias para compatibilidad
      };
      
      console.log('🤖 Enviando mensaje con contexto:', { 
        message: userMessage.content, 
        context 
      });
      
      // Usar el servicio chatApiService
      const result = await sendChatMessage(userMessage.content, context);
      
      // Procesar la respuesta para detectar archivos
      const processedResult = processResponseContent(result.response);
      
      const assistantResponse = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: processedResult.content,
        files: processedResult.files || [],
        timestamp: result.timestamp,
        conversationId: result.conversationId,
        category: result.category,
        responseTime: result.responseTime
      };
      
      setMessages(prev => [...prev, assistantResponse]);
      
    } catch (error) {
      console.error('❌ Error enviando mensaje:', error);
      
      // Usar función de fallback del servicio
      const fallbackContent = getFallbackResponse(
        userMessage.content, 
        isLikelyAdmin ? 'admin' : 'cliente'
      );
      
      const errorMessage = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: `❌ ${fallbackContent}`,
        timestamp: new Date().toISOString(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
      setIsTyping(false);
    }
  };
  
  // Manejar Enter en el input
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };
  
  // Limpiar chat
  const clearChat = () => {
    setMessages([getWelcomeMessage]);
  };
  
  // Reset textarea height when input is cleared
  const resetTextareaHeight = () => {
    if (inputRef.current) {
      const isMobile = window.innerWidth < 768;
      const minHeight = isMobile ? 48 : 40;
      inputRef.current.style.height = minHeight + 'px';
    }
  };
  
  // Calcular si el contexto está listo para mostrar el chat correctamente
  const isContextReady = useMemo(() => {
    const basicRequirements = userLoaded && 
                             !chatConfigLoading && 
                             chatIAEnabled;
                             
    // Para admin, esperar empresa y que no haya errores de carga
    if (isLikelyAdmin) {
      return basicRequirements && !!currentEmpresa;
    }
    
    // Para clientes, solo los requisitos básicos
    return basicRequirements;
  }, [userLoaded, chatConfigLoading, chatIAEnabled, isLikelyAdmin, currentEmpresa]);

  // Debug estado del chat - solo cuando cambian valores relevantes
  useEffect(() => {
    const currentMessageType = messages.length > 0 ? 
      (messages[0].content?.includes('administrador') || messages[0].content?.includes('Como administrador') ? 'admin' : 'cliente') : 'none';
      
    console.log('🤖 [ChatIA] Estado del chat:', { 
      userLoaded, 
      chatConfigLoading, 
      chatIAEnabled,
      isLikelyAdmin,
      hasCurrentEmpresa: !!currentEmpresa,
      isContextReady,
      messagesCount: messages.length,
      firstMessagePreview: messages.length > 0 ? messages[0].content?.substring(0, 50) + '...' : 'none',
      currentMessageType,
      shouldShowChat: isContextReady
    });
  }, [userLoaded, chatConfigLoading, chatIAEnabled, isLikelyAdmin, currentEmpresa, isContextReady, messages]);

  // Detectar cuando el sidebar está abierto (busca el overlay del menú "Más")
  useEffect(() => {
    const checkSidebarMenu = () => {
      // Buscar el overlay del menú "Más" del sidebar
      const overlay = document.querySelector('.fixed.inset-0.bg-black.bg-opacity-50.z-40');
      setSidebarMenuOpen(!!overlay);
    };

    // Verificar inmediatamente
    checkSidebarMenu();

    // Crear un MutationObserver para detectar cambios en el DOM
    const observer = new MutationObserver(checkSidebarMenu);
    
    // Observar cambios en el body (donde se agregan los overlays)
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class']
    });

    return () => observer.disconnect();
  }, []);
  
  // No mostrar el chat si el contexto no está listo
  if (!isContextReady) return null;
  
  return (
    <>
      {/* Contenedor para el botón y badge */}
      <div className={`fixed bottom-20 right-6 md:bottom-6 z-50 transition-all duration-300 ${
        sidebarMenuOpen ? 'opacity-0 pointer-events-none scale-75' : 'opacity-100 pointer-events-auto scale-100'
      }`}>
        <button
          onClick={() => setIsOpen(true)}
          className="relative bg-blue-600 hover:bg-blue-700 text-white rounded-full p-4 shadow-lg hover:shadow-xl transition-all duration-300 group"
          style={{ transform: 'translateZ(0)' }}
          aria-label="Abrir chat IA"
        >
          <FaRobot className="w-6 h-6 group-hover:scale-110 transition-transform duration-200" />
          
          {/* Tooltip */}
          <div className="absolute bottom-full right-0 mb-2 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-all duration-200 z-50">
            <div className="bg-gray-900 border border-gray-700 rounded-lg p-3 w-64 shadow-xl">
              {isLikelyAdmin ? (
                <div>
                  <div className="font-semibold text-white">Asistente IA Administrativo</div>
                  <div className="text-xs text-gray-300 mt-1">
                    Consulta estadísticas, ingresos, clientes y métricas del taller
                  </div>
                </div>
              ) : (
                <div>
                  <div className="font-semibold text-white">Tu Asistente Personal</div>
                  <div className="text-xs text-gray-300 mt-1">
                    Consulta tus vehículos, mantenimientos, precios y citas
                  </div>
                </div>
              )}
              {/* Flecha del tooltip */}
              <div className="absolute top-full right-4 w-2 h-2 bg-gray-900 border-r border-b border-gray-700 transform rotate-45"></div>
            </div>
          </div>
        </button>
      </div>
      
      {/* Modal de Chat */}
      <Modal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title={
          <div>
            <div className="flex items-center gap-3">
              <FaRobot className="text-blue-400 text-xl" />
              <div>
                <h2 className="text-xl font-bold">Asistente IA</h2>
                <p className="text-sm text-gray-400">
                  {isLikelyAdmin
                    ? `Panel Administrativo - ${currentEmpresa?.nombre || 'Tu Empresa'}`
                    : 'Tu asistente personal del taller'
                  }
                </p>
              </div>
            </div>
          </div>
        }
        size="2xl"
        className="h-[700px]"
        headerExtra={
          <Button 
            variant="secondary" 
            size="sm" 
            onClick={clearChat}
            className="text-xs"
          >
            Limpiar
          </Button>
        }
      >
        <div className="flex flex-col h-[600px]">
          {/* Área de mensajes */}
          <div className="flex-1 overflow-y-auto mb-4 space-y-4 pr-2">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`flex items-start gap-3 max-w-[85%] md:max-w-[85%] max-w-[90%] ${
                    message.type === 'user' ? 'flex-row-reverse' : ''
                  }`}
                >
                  {/* Avatar - Solo en desktop */}
                  <div className="hidden md:flex flex-shrink-0 w-8 h-8 rounded-full overflow-hidden items-center justify-center">
                    {message.type === 'user' ? (
                      user?.imageUrl ? (
                        <img 
                          src={user.imageUrl} 
                          alt={user?.firstName || 'Usuario'} 
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-blue-600 text-white flex items-center justify-center">
                          <FaUser className="w-4 h-4" />
                        </div>
                      )
                    ) : (
                      <div className="w-full h-full bg-gray-600 text-white flex items-center justify-center">
                        <FaRobot className="w-4 h-4" />
                      </div>
                    )}
                  </div>
                  
                  {/* Mensaje */}
                  <div
                    className={`p-3 rounded-lg ${
                      message.type === 'user'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-700 text-gray-100'
                    }`}
                  >
                    <div className="whitespace-pre-wrap text-sm md:text-sm text-xs leading-relaxed">
                      {message.content}
                    </div>
                    {/* Mostrar archivos adjuntos si existen */}
                    {message.files && message.files.length > 0 && (
                      <div className="mt-2">
                        {message.files.map((file, fileIndex) => (
                          <FileAttachment key={fileIndex} file={file} />
                        ))}
                      </div>
                    )}
                    <div className="text-xs opacity-70 mt-2">
                      {new Date(message.timestamp).toLocaleTimeString('es-CL', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </div>
                  </div>
                </div>
              </div>
            ))}
            
            {/* Indicador de escritura */}
            {isTyping && (
              <div className="flex justify-start">
                <div className="flex items-start gap-3 max-w-[85%] md:max-w-[85%] max-w-[90%]">
                  {/* Avatar - Solo en desktop */}
                  <div className="hidden md:flex flex-shrink-0 w-8 h-8 rounded-full bg-gray-600 text-white items-center justify-center">
                    <FaRobot className="w-4 h-4" />
                  </div>
                  <div className="bg-gray-700 text-gray-100 p-3 rounded-lg">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>
          
          {/* Input de mensaje */}
          <div className="relative bg-gray-700 border border-gray-600 rounded-lg focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 transition-all">
            <textarea
              ref={inputRef}
              value={inputMessage}
              onChange={(e) => {
                setInputMessage(e.target.value);
                // Auto-expandir textarea hasta 4 filas
                const textarea = e.target;
                const isMobile = window.innerWidth < 768;
                const minHeight = isMobile ? 48 : 40; // Más compacto
                const maxHeight = 104; // 4 líneas máximo
                
                textarea.style.height = 'auto';
                const newHeight = Math.max(minHeight, Math.min(textarea.scrollHeight, maxHeight));
                textarea.style.height = newHeight + 'px';
              }}
              onKeyDown={handleKeyPress}
              placeholder={
                isLikelyAdmin
                  ? "Pregunta sobre estadísticas, clientes, ingresos..." 
                  : "Pregunta sobre tus vehículos, mantenimientos, precios..."
              }
              className="w-full bg-transparent text-white px-4 py-3 pr-12 resize-none focus:outline-none placeholder-gray-400 md:text-base text-base leading-relaxed"
              rows={window.innerWidth < 768 ? 2 : 1}
              style={{ 
                minHeight: window.innerWidth < 768 ? '48px' : '40px', // Más compacto
                maxHeight: '104px', // 4 líneas: 20px * 4 + 24px padding
                fontSize: window.innerWidth < 768 ? '16px' : '14px', // Evita zoom en iOS
                paddingBottom: '12px' // Espacio para el botón
              }}
              disabled={isLoading}
            />
            <button
              onClick={sendMessage}
              disabled={!inputMessage.trim() || isLoading}
              className={`absolute right-2 bottom-2 p-2 rounded-full transition-all ${
                !inputMessage.trim() || isLoading
                  ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 text-white hover:scale-105'
              }`}
            >
              {isLoading ? (
                <FaSpinner className="w-4 h-4 animate-spin" />
              ) : (
                <FaPaperPlane className="w-4 h-4" />
              )}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default ChatIA;